/**
 * Centralized React Query key factory for the Inspections listing.
 *
 * Key structure (contract §7):
 *   ['inspections-listing', scope, normalizedFilters, sort, pageNumber, pageSize]
 *
 * - Multi-select array values are order-normalized (sorted) before hashing.
 * - Scope type/id and sort field/direction are in fixed order.
 * - search is part of the key when present (narrowing changes → new key → page resets).
 * - The feature namespace 'inspections-listing' distinguishes keys from other features
 *   sharing the same QueryClient.
 */
import { buildQueryKeySegment, normalizeMultiSelect } from '@hooks/listings';
export const INSPECTIONS_QUERY_NAMESPACE = 'inspections-listing';
/** Normalized filter snapshot used as part of the query key. */
function normalizeFilters(filters) {
    return {
        scheduledDate: filters.scheduledDate,
        inspectionDate: filters.inspectionDate,
        dueDate: filters.dueDate,
        visitDate: filters.visitDate,
        frequency: normalizeMultiSelect(filters.frequency),
        building: normalizeMultiSelect(filters.building),
        status: normalizeMultiSelect(filters.status),
        technician: normalizeMultiSelect(filters.technician),
        asset: normalizeMultiSelect(filters.asset),
        technicianTeam: normalizeMultiSelect(filters.technicianTeam),
        account: normalizeMultiSelect(filters.account),
        territory: normalizeMultiSelect(filters.territory),
        additionalTerritory: normalizeMultiSelect(filters.additionalTerritory),
        tags: normalizeMultiSelect(filters.tags),
        buildingTags: normalizeMultiSelect(filters.buildingTags),
        accountTags: normalizeMultiSelect(filters.accountTags),
        workOrderTags: normalizeMultiSelect(filters.workOrderTags),
        subcontractor: normalizeMultiSelect(filters.subcontractor),
        inspectionType: normalizeMultiSelect(filters.inspectionType),
        buildingStatus: filters.buildingStatus,
        noAccess: filters.noAccess,
        zipCode: filters.zipCode,
        referenceNumber: filters.referenceNumber,
        workOrder: filters.workOrder,
        inspectionId: filters.inspectionId,
        tce: filters.tce,
        tceErrors: filters.tceErrors,
        irol: filters.irol,
        irolErrors: filters.irolErrors,
        livsafe: filters.livsafe,
        livsafeErrors: filters.livsafeErrors,
        inspectionSeries: filters.inspectionSeries
    };
}
/**
 * Builds the full React Query key for an inspections listing state.
 *
 * @param state — normalized ListingQueryState
 * @param search — current search term (empty string treated as absent)
 */
export function inspectionsQueryKey(state) {
    var _a;
    const segment = buildQueryKeySegment(state.scope, normalizeFilters(state.filters), state.sort, state.page.number, state.page.size);
    return [INSPECTIONS_QUERY_NAMESPACE, (_a = state.search) !== null && _a !== void 0 ? _a : '', ...segment];
}
export const inspectionsKeys = {
    /** Base key — invalidates all inspections listing queries. */
    all: [INSPECTIONS_QUERY_NAMESPACE],
    /** Key scoped to a specific normalized state (used by useQuery). */
    listing: (state) => inspectionsQueryKey(state)
};
